import React from 'react';
import PropTypes from 'prop-types';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import styles from './ApplicantList.module.scss';
import { Button } from 'react-bootstrap';

const ApplicantList = ({
  applicants,
  selectedApplicantId,
  onSelectApplicant,
  showInviteMoreButton,
  browseURL = '/browse',
}) => {
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const createdAt = new Date(date);
    const diffInSeconds = Math.floor((now - createdAt) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  };

  return (
    <div className={styles.listContainer}>
      {applicants.map((applicant) => (
        <button
          key={applicant.id}
          onClick={() => onSelectApplicant(applicant)}
          className={`${styles.applicantCard} ${
            selectedApplicantId === applicant.id ? styles.selected : ''
          }`}
        >
          <div className={styles.cardContent}>
            {/* Avatar */}
            <Thumbnail
              thumbnails={applicant.avatars}
              className={styles.avatar}
              circle
            />

            {/* Content */}
            <div className={styles.details}>
              <div className={styles.nameRow}>
                <h3 className={styles.name}>{applicant.name}</h3>
                {(applicant.isUnread || applicant.hasUnreadMessages) && (
                  <span className={styles.unreadDot} />
                )}
              </div>

              <div className={styles.location}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>{applicant.location}</span>
              </div>

              <div className={styles.roles}>
                {applicant.roles.slice(0, 3).map((role, index) => (
                  <span key={index} className={styles.roleTag}>
                    {role}
                  </span>
                ))}
              </div>

              <div className={styles.footer}>
                <span className={styles.timeAgo}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {formatTimeAgo(applicant.timeAgo)}
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}

      {applicants.length === 0 && (
        <div className={styles.emptyState}>
          <p>No applicants found</p>
        </div>
      )}

      {showInviteMoreButton && applicants.length > 0 && (
        <Button href={browseURL} className={styles.inviteMoreButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          <span>Invite more experts</span>
        </Button>
      )}
    </div>
  );
};

ApplicantList.propTypes = {
  applicants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      avatars: PropTypes.shape({
        images: PropTypes.object,
      }),
      location: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
      timeAgo: PropTypes.string,
    })
  ).isRequired,
  selectedApplicantId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSelectApplicant: PropTypes.func.isRequired,
  showInviteMoreButton: PropTypes.bool,
  browseURL: PropTypes.string,
};

ApplicantList.defaultProps = {
  selectedApplicantId: null,
  showInviteMoreButton: false,
  browseURL: '/browse',
};

export default ApplicantList;
